import { useState } from "react";
import { ChevronDown, Copy, Check, KeyRound, Loader2 } from "lucide-react";

export const prettyBody = (body) => (typeof body === "string" ? body : JSON.stringify(body, null, 2));

export function CopyBtn({ text, getText, label = "Copy", testid, icon: Icon = Copy }) {
    const [state, setState] = useState("idle");
    const copy = async () => {
        try {
            setState("busy");
            const t = getText ? await getText() : text;
            await navigator.clipboard.writeText(t);
            setState("ok"); setTimeout(() => setState("idle"), 1500);
        } catch { setState("idle"); }
    };
    return (
        <button type="button" onClick={copy} data-testid={testid} disabled={state === "busy"}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-900 transition-colors">
            {state === "ok" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : state === "busy" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
            {state === "ok" ? "Copied" : label}
        </button>
    );
}

export const buildVendorReport = (order, req, res) => [
    `Payment ref id: ${order.client_reference_id || "-"}`,
    `Order id: ${order.order_id}`,
    `Vendor payment id: ${order.payspher_payment_id ?? "-"}`,
    `Status: ${order.status}${order.response_message ? ` — ${order.response_message}` : ""}`,
    `Outbound server IP: ${res?.egress_ip || "-"}`,
    "",
    "========== REQUEST ==========",
    req ? `POST ${req.endpoint}\nContent-Type: application/json\nSent at: ${req.sent_at}\n\n${prettyBody(req.body)}` : "(not recorded — order created before request logging was enabled)",
    "",
    "========== RESPONSE ==========",
    res ? `HTTP ${res.http_status ?? "network error"}\nReceived at: ${res.received_at}\n\n${prettyBody(res.body)}` : "(no response recorded)",
    ...((order.vendor_webhooks || []).length ? ["", "========== INBOUND WEBHOOKS (to our notification_url) ==========",
        ...order.vendor_webhooks.slice().reverse().map((w) => [
            `--- ${w.received_at} · ${w.outcome} · from ${w.source_ip || "-"}`,
            w.signature ? `Received signature: ${w.signature.received || "(none)"}\nSigned plain text: ${w.signature.plain_text}` : "",
            w.body,
        ].filter(Boolean).join("\n"))] : []),
].join("\n");

const Meta = ({ k, v }) => (
    <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div>
        <div className="text-xs font-mono text-slate-700 truncate" title={String(v ?? "")}>{v ?? "—"}</div>
    </div>
);

const Pre = ({ children, testid }) => (
    <pre data-testid={testid} className="text-[11px] leading-relaxed font-mono bg-slate-50 border border-slate-100 rounded p-2.5 max-h-64 overflow-auto whitespace-pre-wrap break-all text-slate-700">{children}</pre>
);

/** Collapsible Request | Response log for a PayIn order. `loadFull` (admin only) returns {request, response} with the full auth key. */
const OUTCOME_CLS = { ok: "bg-emerald-100 text-emerald-800", signature_mismatch: "bg-rose-100 text-rose-800" };

function WebhookList({ hooks, testid }) {
    return (
        <div className="space-y-2" data-testid={`${testid}-webhooks`}>
            {hooks.map((w, i) => (
                <div key={i} className="border border-slate-100 rounded p-2 space-y-1.5" data-testid={`${testid}-webhook-${i}`}>
                    <div className="flex flex-wrap items-center gap-2 text-[11px]">
                        <span className={`px-1.5 py-0.5 rounded font-semibold ${OUTCOME_CLS[w.outcome] || "bg-amber-100 text-amber-800"}`}>{w.outcome.replace(/_/g, " ")}</span>
                        <span className="text-slate-500">{new Date(w.received_at).toLocaleString()}</span>
                        <span className="text-slate-400 font-mono ml-auto">from {w.source_ip || "—"}</span>
                    </div>
                    {w.signature && (
                        <div className="text-[10px] font-mono text-slate-600 space-y-0.5 break-all">
                            <div><span className="text-slate-400">received </span>{w.signature.received || "(none)"}</div>
                            <div><span className="text-slate-400">signed   </span>{w.signature.plain_text}</div>
                        </div>
                    )}
                    <Pre>{w.body}</Pre>
                </div>
            ))}
        </div>
    );
}

export function VendorResponse({ order, loadFull, testid = "vendor-log" }) {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("request");
    const req = order?.vendor_request, res = order?.vendor_response;
    const hooks = (order?.vendor_webhooks || []).slice().reverse();
    if (!req && !res && !hooks.length) return null;
    const fullText = async () => { const r = await loadFull(); return buildVendorReport(order, r.request, r.response); };
    return (
        <div className="border border-slate-200 rounded-md bg-white" data-testid={testid}>
            <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-colors">
                <span className="font-medium">Request &amp; Response</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="border-t border-slate-100 p-3 space-y-3" data-testid={`${testid}-body`}>
                    <div className="inline-flex bg-slate-100 rounded-md p-0.5">
                        {["request", "response", ...(hooks.length ? ["webhooks"] : [])].map((t) => (
                            <button key={t} type="button" onClick={() => setTab(t)} data-testid={`${testid}-tab-${t}`}
                                    className={`px-3 py-1 rounded text-[11px] font-medium capitalize transition-colors ${tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>{t}</button>
                        ))}
                    </div>
                    {tab === "webhooks" ? <WebhookList hooks={hooks} testid={testid} /> : tab === "request" ? (req ? (
                        <>
                            <div className="grid grid-cols-2 gap-3"><Meta k="Endpoint" v={`POST ${req.endpoint}`} /><Meta k="Sent" v={req.sent_at ? new Date(req.sent_at).toLocaleString() : null} /></div>
                            <Pre testid={`${testid}-request`}>{prettyBody(req.body)}</Pre>
                        </>
                    ) : <div className="text-xs text-slate-400 italic py-3" data-testid={`${testid}-request-missing`}>Not recorded — this order was created before request logging was enabled. Create a new order to capture it.</div>
                    ) : (res ? (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <Meta k="HTTP status" v={res.http_status ?? "network error"} />
                                <Meta k="Outbound IP" v={res.egress_ip} />
                                <Meta k="Received" v={res.received_at ? new Date(res.received_at).toLocaleString() : null} />
                            </div>
                            <Pre testid={`${testid}-response`}>{prettyBody(res.body)}</Pre>
                        </>
                    ) : <div className="text-xs text-slate-400 italic py-3">No response recorded.</div>)}
                    <div className="flex flex-wrap justify-end gap-4 pt-1 border-t border-slate-100">
                        <CopyBtn text={buildVendorReport(order, req, res)} label="Copy for vendor" testid={`${testid}-copy`} />
                        {loadFull && <CopyBtn getText={fullText} icon={KeyRound} label="Copy with full auth key" testid={`${testid}-copy-full`} />}
                    </div>
                </div>
            )}
        </div>
    );
}
